import { Badge, Card, Grid, Group, Text, Title } from '@mantine/core';
import { IconArrowDown, IconArrowUp, IconMinus } from '@tabler/icons-react';
import { useSelector } from 'react-redux';
import SummaryCardsIcons from '../../constants/SummaryCardsIcons';
import prettifiedLabel from '../../constants/labelPrettifier';
import { isChannelConfigured } from '../../helpers/domainChannels';

const SummaryCard = ({ currentPeriodData, previousPeriodData, currency, size }) => {
  const facebook = useSelector((state) => state.facebook);
  const domain = useSelector((state) => state.domain);
  const whatsappAccount = useSelector((state) => state.whatsapp.account);
  const formatOptions = currency ? { style: 'currency', currency } : {};

  // Guard against non-array data (e.g., empty string '')
  if (!Array.isArray(currentPeriodData)) {
    return null;
  }

  const prevData = Array.isArray(previousPeriodData) ? previousPeriodData : [];

  const SummaryCardItem = ({ label, value, previousValue }) => {
    // Calculate percentage difference
    const percentDiff = previousValue !== 0 ? Math.abs((value - previousValue) / previousValue) * 100 : (value !== 0 ? 100 : 0);

    let indicator = null;
    if (previousValue !== value) {
      if (percentDiff < 3) {
        // Less than 3% difference - show neutral indicator
        indicator = <IconMinus size={24} style={{ color: 'var(--mantine-color-dimmed)' }} />;
      } else if (previousValue > value) {
        indicator = <IconArrowDown size={24} style={{ color: 'var(--mantine-color-danger-5)' }} />;
      } else {
        indicator = <IconArrowUp size={24} style={{ color: 'var(--mantine-color-success-5)' }} />;
      }
    }

    const cardIcon = SummaryCardsIcons[label]?.tablerIcon || null;

    return (
      <Grid.Col span={{ base: 12, md: size }} key={label}>
        <Card
          padding="sm"
          radius="md"
          withBorder
          style={{ position: 'relative' }}
          className="summary-card"
        >
          <style>{`
            .summary-card .previous-value {
              opacity: 0;
              transition: opacity 0.2s ease;
            }
            .summary-card:hover .previous-value {
              opacity: 1;
            }
          `}</style>
          <Group justify="flex-end" mb={4}>
            <Badge
              size="lg"
              radius="sm"
              leftSection={cardIcon}
              styles={{ label: { textTransform: 'none', fontWeight: 400 } }}
            >
              {prettifiedLabel(label)}
            </Badge>
          </Group>
          <Group gap="xs" justify="center" align="center" py="xs">
            <Title order={3}>{value?.toLocaleString(navigator.language, formatOptions)}</Title>
            {indicator}
          </Group>
          <Text
            size="sm"
            c="dimmed"
            ta="right"
            className="previous-value"
          >
            Previous: {previousValue?.toLocaleString(navigator.language, formatOptions)}
          </Text>
        </Card>
      </Grid.Col>
    );
  };

  // Which channels the domain has credentials for is shared with the At a Glance
  // tab's channel tiles, so both hide exactly the same channels.
  const channelSources = { domain: domain.domain, whatsappAccount };

  const summaryCards = currentPeriodData
    .filter((stat) => stat.id !== 'other')
    .map((el) => {
      if (['textMessage', 'textMessageSends'].includes(el.id) && !isChannelConfigured('textMessage', channelSources)) {
        return null;
      }

      if (
        ['viberMessage', 'viberMessageSends'].includes(el.id) &&
        !isChannelConfigured('viberMessage', channelSources)
      ) {
        return null;
      }

      if (el.id === 'relevaFacebookAd' && !facebook.user) {
        return null;
      }

      if (
        ['whatsappMessage', 'whatsappMessageSends'].includes(el.id) &&
        !isChannelConfigured('whatsappMessage', channelSources)
      ) {
        return null;
      }

      switch (el.id) {
        case 'recommender':
          el.label = 'productBlock';
          break;
        case 'recommenderClicks':
          el.label = 'productBlockClicks';
          break;
        case 'recommenderPurchases':
          el.label = 'productBlockPurchases';
          break;
        default:
          break;
      }

      const { id, label, value } = el;
      const prevEl = prevData.find((tempEl) => tempEl.id === id);
      const previousValue = prevEl?.value || 0;

      if (!value && !previousValue) {
        return null;
      }

      return <SummaryCardItem key={id} label={label} value={value || 0} previousValue={previousValue} />;
    });

  return summaryCards;
};

export default SummaryCard;
